'use client';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import TestGoalDistance from '@/components/test/insights/test-goal-distance';
import TestNextSteps from '@/components/test/insights/test-next-steps';
import TestProgressTrend from '@/components/test/insights/test-progress-trend';
import TestSectionPerformance from '@/components/test/insights/test-section-performance';
import { useCourses } from '@/providers';
import type { TestResult } from '@/types';
import { getFirstExercise } from '@/utils/client/course-curriculum';
import { useRoute } from '@/utils/client/route';
import {
  getCohortResults,
  getFeedbackTone,
  getImprovementDelta,
  getOverallPercentage,
  testAtMatches,
} from '@/utils/client/test-feedback';

export default function TestSubmissionSummary({
  selectedResult,
  allResults,
  selectedIndex,
}: {
  selectedResult: TestResult;
  allResults: TestResult[];
  selectedIndex: number;
}) {
  const { t } = useTranslation();
  const { goToRoute } = useRoute();
  const { selectedCourse } = useCourses();

  const { createdAt, statistics, statisticsSections, testRun } = selectedResult;
  const runNumber = testRun ?? allResults.length - selectedIndex;
  const previousResult = allResults[selectedIndex + 1];
  const delta = getImprovementDelta(selectedResult, previousResult);
  const tone = getFeedbackTone(delta);
  const overallPercentage = getOverallPercentage(statistics);
  const latestTestAt = allResults[0]?.testAt;
  const cohortRuns = getCohortResults(allResults, latestTestAt);
  const isInCurrentCohort = testAtMatches(selectedResult.testAt, latestTestAt);
  const firstExercise = selectedCourse ? getFirstExercise(selectedCourse.curriculum) : undefined;
  const fallbackSlug = firstExercise?.item.slug;

  const onViewResults = () => {
    if (!fallbackSlug || !selectedCourse) return;
    goToRoute('myCourseLearn', {
      courseSlug: selectedCourse.slug,
      chapterSlug: fallbackSlug,
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('test.insight.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('test.results.runLabel', {
              run: runNumber,
              date: dayjs(createdAt).format('DD.MM.YYYY HH:mm'),
            })}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {t('test.insight.scoreLine', {
              correct: statistics.correct,
              questions: statistics.questions,
              percentage: overallPercentage,
            })}
          </Typography>
          {delta !== undefined && delta !== 0 && (
            <Chip
              size="small"
              label={delta > 0 ? `+${delta}%` : `${delta}%`}
              color={delta > 0 ? 'success' : 'warning'}
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      <Alert severity="info" variant="outlined" sx={{ mb: 4 }}>
        {t(`test.insight.coaching.${tone}`, {
          percentage: overallPercentage,
          delta: Math.abs(delta ?? 0),
        })}
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <TestProgressTrend testResults={allResults} delta={delta} />
        <TestSectionPerformance sections={statisticsSections} />
        {isInCurrentCohort && (
          <TestGoalDistance
            statistics={statistics}
            cohortRuns={cohortRuns}
            currentResultId={selectedResult._id}
          />
        )}
        {selectedCourse && (
          <TestNextSteps
            sections={statisticsSections}
            curriculum={selectedCourse.curriculum}
            courseSlug={selectedCourse.slug}
            fallbackSlug={fallbackSlug}
          />
        )}
      </Box>

      {fallbackSlug && selectedIndex === 0 && (
        <Box sx={{ mt: 4 }}>
          <Button variant="contained" color="primary" onClick={onViewResults}>
            {t('test.summary.viewResults')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
